import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, from, of } from 'rxjs';
import { ProposalRequest, ProposalRequestFile } from 'src/app/classes/models';
import { File as FileModel } from 'src/app/classes/models/file';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { ProposalRequestService } from 'src/app/services/proposal-request.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-proposal-request-file-upload-modal',
  templateUrl: './proposal-request-file-upload-modal.component.html',
  styleUrls: ['./proposal-request-file-upload-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ProposalRequestFileUploadModalComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private ProposalRequestService: ProposalRequestService,
    private ActiveModal: NgbActiveModal
  ) {}

  // Save the Proposal Request with the Files attached to it.
  @Input() ProposalRequest!: ProposalRequest;
  @Input() ProposalRequestFiles: ProposalRequestFile[] = [];
  @Input() title: string = '';

  Files: FileModel[] = [];
  fileErrors: GenericObject = {};

  setFilesFromProposalRequestFiles() {
    this.Files.splice(0);
    this.ProposalRequestFiles.forEach((prf) => {
      const { File } = prf;

      if (!File) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Proposal request file is missing File information.',
          { returnError: true }
        );
      }

      this.Files.push(File);
    });
    this._cdr.markForCheck();
  }

  fileUpload({ data: File }: { data: File }) {
    const { name: filename, size: filesize, type: filetype } = File;

    if ((Number(filesize) || 0) > 10 * 1000000) {
      this.fileErrors['isFileTooBig'] = true;
      throw new Error('Cannot add file. File is greater than 10 MB.');
    }

    const totalFileSize = this.ProposalRequestFiles.reduce((accum, prf) => {
      const { File: { filesize } = {} } = prf;

      return accum + Number(filesize);
    }, 0);

    if (totalFileSize + Number(filesize) > 25 * 1000000) {
      this.fileErrors['isFileTooBig'] = true;
      throw new Error('Cannot add file. Files total more than 25 MB.');
    }

    delete this.fileErrors['isFileTooBig'];

    from(File.arrayBuffer()).subscribe({
      next: (FileArrayBuffer) => {
        const FileString = `data:${filetype};base64,${this.UtilityService.convertArrayBufferToBase64EncodedString(FileArrayBuffer)}`;

        const { id } = this.ProposalRequest;

        const ProposalRequestFileRecord = new ProposalRequestFile({
          ProposalRequestId: id,
          File: {
            filename,
            filesize,
            filetype,
            FileContent: {
              filecontents: FileString
            }
          }
        });

        this.ProposalRequestFiles.push(ProposalRequestFileRecord);
        this.setFilesFromProposalRequestFiles();
        this._cdr.markForCheck();
      }
    });
  }

  removeProposalRequestFile({ index }: { index: number }) {
    const ProposalRequestFile = this.ProposalRequestFiles.splice(index, 1)[0];
    const { id: ProposalRequestFileId } = ProposalRequestFile;
    Object.assign(this.ProposalRequest, {
      ProposalRequestFiles: this.ProposalRequestFiles
    });

    if (ProposalRequestFileId) {
      this.ProposalRequestService.deleteProposalRequestFile(
        ProposalRequestFile
      ).subscribe({
        next: () => {
          this.setFilesFromProposalRequestFiles();
          this._cdr.markForCheck();
        }
      });
    } else {
      this.setFilesFromProposalRequestFiles();
      this._cdr.markForCheck();
    }
  }

  close() {
    this.ActiveModal.dismiss();
  }

  save() {
    (() => {
      if (this.ProposalRequestFiles.length > 0) {
        return forkJoin(
          this.ProposalRequestFiles.map((ProposalRequestFileRecord) =>
            this.ProposalRequestService.saveProposalRequestFile(
              ProposalRequestFileRecord
            )
          )
        );
      }

      return of([]);
    })().subscribe({
      next: (ProposalRequestFiles) => {
        this.ActiveModal.close(ProposalRequestFiles);
      }
    });
  }

  ngOnInit(): void {
    if (!this.ProposalRequest) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Proposal Request.',
        { returnError: true }
      );
    }

    this.setFilesFromProposalRequestFiles();
  }
}
